import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

// ============================================================
// Asset types
// ============================================================

export interface GameAssets {
  readonly f16: THREE.Group;
  readonly shahed: THREE.Group;
  readonly carrier: THREE.Group;
}

// ============================================================
// Loader setup
// ============================================================

function createLoader(): GLTFLoader {
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('/draco/');

  const gltfLoader = new GLTFLoader();
  gltfLoader.setDRACOLoader(dracoLoader);

  return gltfLoader;
}

function loadGLB(loader: GLTFLoader, url: string): Promise<THREE.Group> {
  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => resolve(gltf.scene),
      undefined,
      (error) => reject(new Error(`Failed to load ${url}: ${error}`))
    );
  });
}

// ============================================================
// Model preparation — fix orientation and scale
// ============================================================

function prepareVehicleModel(
  model: THREE.Group,
  scale: number,
  rotationY: number,
): THREE.Group {
  const wrapper = new THREE.Group();

  model.scale.setScalar(scale);
  model.rotation.y = rotationY;

  wrapper.add(model);
  return wrapper;
}

// ============================================================
// Public API
// ============================================================

export async function loadCoreAssets(): Promise<GameAssets> {
  const loader = createLoader();

  const [f16Raw, shahedRaw, carrierRaw] = await Promise.all([
    loadGLB(loader, '/models/f16.glb'),
    loadGLB(loader, '/models/shahed.glb'),
    loadGLB(loader, '/models/carrier.glb'),
  ]);

  const f16 = prepareVehicleModel(f16Raw, 5, -Math.PI / 2);
  const shahed = prepareVehicleModel(shahedRaw, 7.5, -Math.PI / 2);
  const carrier = prepareVehicleModel(carrierRaw, 1, 0);

  return { f16, shahed, carrier };
}
